import { createTheme } from '@mui/material/styles';

const palette = {
  primary: {
    main: '#0D0D0D',
    light: '#2A2A2A',
    dark: '#000000',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#F20505',
    light: '#FF3838',
    dark: '#8C0303',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#8C0303',
    light: '#B91C1C',
    dark: '#5F0202',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#D97706',
    light: '#F59E0B',
    dark: '#B45309',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#1F2937',
    light: '#374151',
    dark: '#111827',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#15803D',
    light: '#22C55E',
    dark: '#14532D',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F4F4F5',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#0D0D0D',
    secondary: '#52525B',
    disabled: '#A1A1AA',
  },
  divider: '#E4E4E7',
  grey: {
    50: '#FAFAFA',
    100: '#F4F4F5',
    200: '#E4E4E7',
    300: '#D4D4D8',
    400: '#A1A1AA',
    500: '#71717A',
    600: '#52525B',
    700: '#3F3F46',
    800: '#27272A',
    900: '#18181B',
  },
  // Paleta semantica para estados de tareas. Reemplaza la lógica
  // hardcoded de getStatusBackgroundColor en TaskSummaryTable.
  statuses: {
    pending:     { bg: '#FEF3C7', fg: '#92400E', main: '#D97706' },
    inProgress:  { bg: '#DBEAFE', fg: '#1E40AF', main: '#2563EB' },
    forApproval: { bg: '#FEE2E2', fg: '#8C0303', main: '#F20505', border: '#F20505' },
    received:    { bg: '#E0E7FF', fg: '#3730A3', main: '#4338CA' },
    done:        { bg: '#DCFCE7', fg: '#166534', main: '#15803D' },
    canceled:    { bg: '#F4F4F5', fg: '#52525B', main: '#71717A' },
    blocked:     { bg: '#FEE2E2', fg: '#8C0303', main: '#8C0303' },
    warranty:    { bg: '#F0F9FF', fg: '#075985', main: '#0284C7' },
    review:      { bg: '#F5F3FF', fg: '#5B21B6', main: '#7C3AED' },
    open:        { bg: '#0D0D0D', fg: '#FFFFFF', main: '#0D0D0D' },
    requested:   { bg: '#FFEDD5', fg: '#9A3412', main: '#EA580C' },
    approved:    { bg: '#ECFDF5', fg: '#065F46', main: '#059669' },
    denied:      { bg: '#FFE4E6', fg: '#9F1239', main: '#BE123C' },
  },
  // Paleta para gráficos: monocromática + acento rojo Kepler.
  // Usada por Recharts en TaskSummaryTable.
  charts: [
    '#0D0D0D',
    '#F20505',
    '#52525B',
    '#A1A1AA',
    '#8C0303',
    '#27272A',
    '#D4D4D8',
    '#F59E0B',
    '#15803D',
    '#2563EB',
  ],
};

const theme = createTheme({
  palette,
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `2px solid ${palette.secondary.main}`,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${palette.divider}`,
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        rounded: { borderRadius: 12 },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingInline: 16,
          paddingBlock: 8,
        },
        containedPrimary: {
          '&:hover': { backgroundColor: palette.primary.light },
        },
        containedSecondary: {
          '&:hover': { backgroundColor: palette.secondary.dark },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 6 },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.primary.main,
          fontSize: 12,
          fontWeight: 500,
        },
        arrow: { color: palette.primary.main },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
  },
});

export default theme;
